import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { type RespondedWith, responseScheme } from '@/models/response';
import { useMessageStore } from '@/stores/message';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';

const getQueryKey = (type: ConversationType, id: Nullable<number>) => [
  type === 'session' ? '/GetDeleteChat' : '/GetDeleteGroupChat',
  id,
];

export const generateDeletedMessagesQueryOptions = (type: ConversationType, id: Nullable<number>) =>
  queryOptions({
    queryKey: getQueryKey(type, id),
    queryFn: id
      ? async ({ signal }) => {
          const prevData = queryClient.getQueryData<{ updateTime?: number; data: number[] }>(getQueryKey(type, id));

          const currentUpdateTime = Math.floor(Date.now() / 1000);

          const response = await fetcher<RespondedWith<number[]>>(
            type === 'session' ? '/GetDeleteChat' : '/GetDeleteGroupChat',
            {
              [type === 'session' ? 'ChatSessionID' : 'GroupID']: id,
              UpdateTime: prevData?.updateTime ?? 0,
            },
            {
              signal,
              validResponseCodes: [0],
              schema: responseScheme.extend({ Data: z.array(z.number()).nullish() }),
            },
          );

          const result = response?.Data ?? [];
          if (result.length > 0) useMessageStore.getState().deleteMany(result, type, id);

          return {
            data: [...(prevData?.data ?? []), ...result],
            updateTime: currentUpdateTime,
          };
        }
      : skipToken,
    select: (data) => data.data,
    staleTime: 0,
  });

export default function useDeletedMessages(type: ConversationType, id: Nullable<number>) {
  return useQuery(generateDeletedMessagesQueryOptions(type, id));
}
